// Two-pane source-map review page — the locked Option B redesign from
// design/UI : UX v1.0/for-engineering-ingest-review/. Ships the
// visibility layer the brief called for: reviewer sees the source
// text with each proposed section highlighted so boundary correctness
// becomes VISIBLE instead of inferred.
//
// Route: /admin/knowledge/sources/:id/map  (swapped in MR 9 — this
// page fully replaces the previous single-pane SourceDocumentMapPage
// which has been deleted).
//
// This MR (3/9): the SHELL only —
//   - Route wiring + data load (getSourceMap → ResolvedBookStructure)
//   - AdminShell + PageHeader + status pill
//   - Two-pane grid with a resizable divider (persisted per-user via
//     localStorage; graceful default 44%)
//   - Sticky footer with Reject / Save / Approve buttons (non-
//     functional placeholders; MR 8 wires the state machine)
//   - Loading / error / not-found / propose-still-running states
//   - Left + right pane placeholders (MR 4 replaces left with the
//     ported <MapTree>; MR 5 replaces right with the read-only
//     <SourceDocPane>)
//
// Meta line + status pill copy come straight from the handoff spec at
// design/UI : UX v1.0/for-engineering-ingest-review/specs/map-review-
// strings.md. i18n keys stay minimal for the shell; MR 9 folds in the
// full EN/FR string surface from the spec.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Button, Spinner } from '@gc-coach/design-system';
import type {
  BookChapterDef,
  BookStructureAuditEntry,
  BookSubSectionDef,
  MapGap,
  ParagraphOverride,
  ResolvedBookStructure,
  ResolvedChapter,
  ResolvedSubSection,
} from '@gc-coach/shared';
// Client-side resolver — same helpers the /map endpoint uses; moved to
// @gc-coach/shared in MR 8a so we can re-run them locally after every
// edit to give the reviewer live re-flow of section spans + gaps.
// `deriveHeaderPatternFromText` is the client entry point for the same
// pattern-derivation the chunker uses at ingest time (shared so a
// preview here agrees bit-for-bit with what gets persisted).
import {
  deriveGaps,
  deriveHeaderPatternFromText,
  estimateTokens,
  resolveChapters,
} from '@gc-coach/shared';
import { AdminShell, PageHeader } from '../shell/index.js';
import { useI18n } from '../i18n/index.js';
import {
  getSourceMap,
  patchBookStructure,
  approveBookStructure,
  rejectBookStructure,
  proposeMap,
  listBookStructureAudit,
} from '../api/endpoints.js';
import { MapTree, type MapTreeFocus } from './MapTree.js';
import {
  SourceDocPane,
  type GapAction,
  type SectionAction,
  type SelectionAction,
} from './SourceDocPane.js';
import styles from './MapReviewPage.module.css';

/** localStorage key for the persisted divider position (0..1 fraction). */
const DIVIDER_STORAGE_KEY = 'gc-coach.map-review.divider';
/** Divider bounds — never let either pane collapse below these fractions. */
const DIVIDER_MIN = 0.25;
const DIVIDER_MAX = 0.75;
const DIVIDER_DEFAULT = 0.44;

export function MapReviewPage() {
  const { id: sourceDocId } = useParams<{ id: string }>();
  const { t } = useI18n();

  const [map, setMap] = useState<ResolvedBookStructure | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [dividerFrac, setDividerFrac] = useState<number>(readStoredDivider);

  // Local-edit state — the chapters array is a mutated copy of the
  // fetched map's chapters. Mutations from the tree (rename, pattern
  // edit, add/remove sub-section) replace items here; MR 8 will POST
  // this to PATCH /book-structures/{id} on Save.
  //
  // char_start / char_end offsets on locally-edited sections stay at
  // their originally-resolved values — we do NOT re-run the resolver
  // client-side on each keystroke. Right pane paints the current-load
  // spans; Save round-trips the map and reloads with fresh offsets.
  const [chapters, setChapters] = useState<ResolvedChapter[]>([]);
  const [currentChapterIdx, setCurrentChapterIdx] = useState(0);
  // Baseline copy of the fetched chapters — used for dirty tracking.
  // Never mutated; replaced only when a Save round-trip returns fresh
  // server data (so the "clean" reference always matches persisted
  // state).
  const [baselineChapters, setBaselineChapters] = useState<ResolvedChapter[]>([]);
  // In-flight state for the footer action buttons.
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [reproposing, setReproposing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // MR 6 — unsaved-changes bar + discard confirm + toast pattern.
  const [discardModalOpen, setDiscardModalOpen] = useState(false);
  // MR 10 — toast now carries an optional UNDO affordance (inline button
  // + ⌘Z shortcut both call the same callback). `undoable: true` means
  // the toast's UNDO fires the top of the undo stack — the common case.
  const [toast, setToast] = useState<{ message: string; undoable: boolean } | null>(null);
  // MR 10 — undo/redo. Each entry is a full chapters snapshot taken BEFORE
  // the mutation that created it, so popping replays the pre-mutation
  // state exactly. Cleared whenever a Save round-trips (new baseline =
  // new history horizon) or when the chapter view is swapped (focus
  // reset would make partial undo nonsensical).
  const [undoStack, setUndoStack] = useState<ResolvedChapter[][]>([]);
  // redoStack is read via its setter's callback form only — no UI
  // currently reads the array itself. The underscore prefix tells
  // tsc/eslint that it's intentional, not a leftover.
  const [_redoStack, setRedoStack] = useState<ResolvedChapter[][]>([]);
  // MR 10 — history drawer open/closed. Drawer fetches audit entries
  // lazily when first opened; closing keeps the fetched list in memory
  // so re-open is instant until the next Save.
  const [historyOpen, setHistoryOpen] = useState(false);
  // Focused section from the tree — MR 5 hooks the right pane to react
  // to this (scroll into view + highlight). Kept in page state so both
  // panes can read it once the right pane arrives.
  const [focused, setFocused] = useState<MapTreeFocus | null>(null);

  useEffect(() => {
    if (!sourceDocId) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    void (async () => {
      const res = await getSourceMap(sourceDocId);
      if (cancelled) return;
      setLoading(false);
      if (res.ok) {
        setMap(res.data.map);
        setChapters(res.data.map.chapters);
        setBaselineChapters(res.data.map.chapters);
        setCurrentChapterIdx(0);
        setActionError(null);
      } else {
        setLoadError(res.message ?? 'Failed to load map.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sourceDocId]);

  // chaptersRef lets undo/redo + onChapterChange capture the current
  // chapters without re-creating the callbacks on every setChapters.
  const chaptersRef = useRef<ResolvedChapter[]>([]);
  useEffect(() => {
    chaptersRef.current = chapters;
  }, [chapters]);

  const onChapterChange = useCallback((next: ResolvedChapter) => {
    // Push the CURRENT chapters snapshot onto the undo stack BEFORE
    // applying the mutation, and clear the redo stack (a new edit
    // invalidates any redo future). Snapshots are shallow copies of
    // the chapters array; individual ResolvedChapter objects are not
    // mutated in place, so sharing refs across snapshots is safe.
    setUndoStack((prev) => [...prev, chaptersRef.current]);
    setRedoStack([]);
    setChapters((prev) =>
      prev.map((c, i) => (i === currentChapterIdxRef.current ? next : c)),
    );
  }, []);

  const undo = useCallback(() => {
    setUndoStack((prev) => {
      if (prev.length === 0) return prev;
      const popped = prev[prev.length - 1]!;
      setRedoStack((r) => [...r, chaptersRef.current]);
      setChapters(popped);
      // Hide the toast — its action is now consumed. Keeps the UI
      // honest about what the next ⌘Z would do.
      setToast(null);
      return prev.slice(0, -1);
    });
  }, []);

  const redo = useCallback(() => {
    setRedoStack((prev) => {
      if (prev.length === 0) return prev;
      const popped = prev[prev.length - 1]!;
      setUndoStack((u) => [...u, chaptersRef.current]);
      setChapters(popped);
      setToast(null);
      return prev.slice(0, -1);
    });
  }, []);

  /** Emit a toast message. `undoable` defaults to true since most of
   *  the toast-emitting paths are reducer-driven and have a sensible
   *  undo (just pop the undo stack); Save success + similar non-reducer
   *  events pass false. */
  const emitToast = useCallback(
    (message: string, opts: { undoable?: boolean } = {}) => {
      setToast({ message, undoable: opts.undoable ?? true });
    },
    [],
  );
  // Ref-mirror of currentChapterIdx so the memoized onChapterChange
  // above doesn't need currentChapterIdx in its deps (which would
  // rebuild the callback on every chapter switch and force the tree
  // to re-render more than it needs to).
  const currentChapterIdxRef = useRef(0);
  useEffect(() => {
    currentChapterIdxRef.current = currentChapterIdx;
  }, [currentChapterIdx]);

  const onPrevChapter = useCallback(() => {
    setCurrentChapterIdx((i) => Math.max(0, i - 1));
    setFocused(null);
  }, []);
  const onNextChapter = useCallback(() => {
    setCurrentChapterIdx((i) => Math.min(chapters.length - 1, i + 1));
    setFocused(null);
  }, [chapters.length]);

  // Right-pane action handlers. Mutations produce a new ResolvedChapter
  // via onChapterChange (same pathway as the tree's inline edits).
  // char_start/char_end on new/moved sections are set to null — the
  // right pane paints them absent until the map is Saved + reloaded
  // (server re-runs resolveBookStructure on GET /map). This is honest
  // rendering: we're not going to guess offsets client-side; edits
  // become visible once the server round-trips.
  // ─── Client-side re-resolve (MR 8b) ────────────────────────────────
  //
  // Every edit that changes editable fields (title / header_pattern /
  // sub-section presence / excluded) invalidates the char offsets on
  // the resolved chapters + the derived gaps. Re-run resolveChapters +
  // deriveGaps locally so the reviewer sees the right pane re-flow
  // without waiting for a server round-trip.
  //
  // Uses the same helpers the /map endpoint uses (moved to shared in
  // MR 8a) — client-side preview and server-computed state stay
  // pixel-identical.
  const { chapters: liveChapters, gaps: liveGaps } = useMemo(() => {
    if (!map) return { chapters: [] as ResolvedChapter[], gaps: [] as MapGap[] };
    const base = chapters.map(stripResolvedChapter);
    const resolved = resolveChapters(map.source_text, base);
    const derivedGaps = deriveGaps(map.source_text, resolved);
    return { chapters: resolved, gaps: derivedGaps };
  }, [chapters, map]);

  // MR 9 — live token recompute. After live re-resolve gives every
  // sub-section fresh char offsets, we can slice its body from
  // source_text and estimate tokens locally via the shared
  // estimateTokens (MR 1). The chapter's own count is the same char/4
  // over its whole slice — same semantic as the chunker's propose-time
  // pass, computed live so a Split that shrinks §1.4 from 1,944 → 912
  // updates the badge immediately without waiting for Save + reload.
  //
  // Falls back to the persisted estimated_tokens when char offsets
  // aren't available (unresolved sub / chapter — the propose-time
  // value is the best we have to show).
  const liveTokenChapters = useMemo(() => {
    if (!map) return liveChapters;
    const src = map.source_text;
    return liveChapters.map((ch) => {
      const chStart = ch.char_start;
      const chEnd = ch.char_end;
      const chTokens =
        chStart !== null && chEnd !== null
          ? estimateTokens(src.slice(chStart, chEnd))
          : ch.estimated_tokens;
      const nextSubs: ResolvedSubSection[] = ch.sub_sections.map((sub) => {
        const s = sub.char_start;
        const e = sub.char_end;
        // Only override when we can actually compute — otherwise leave
        // the sub's persisted value in place (it's the best we've got
        // for unresolved subs). Conditional spread keeps the field's
        // narrow `number` type intact for exactOptionalPropertyTypes.
        if (s !== null && e !== null) {
          const live = estimateTokens(src.slice(s, e));
          if (live === sub.estimated_tokens) return sub;
          return { ...sub, estimated_tokens: live };
        }
        return sub;
      });
      const changed =
        chTokens !== ch.estimated_tokens ||
        nextSubs.some((s, i) => s !== ch.sub_sections[i]);
      if (!changed) return ch;
      const next: ResolvedChapter =
        chTokens !== undefined
          ? { ...ch, estimated_tokens: chTokens, sub_sections: nextSubs }
          : { ...ch, sub_sections: nextSubs };
      return next;
    });
  }, [liveChapters, map]);

  // Gap + section handlers read from LIVE state (post-edit re-resolve),
  // not the underlying `chapters` slice — the reviewer clicked on
  // what they see, which is the re-flowed live view. Refs keep the
  // closures fresh without churning callback identity per render.
  const liveGapsRef = useRef<readonly MapGap[]>([]);
  const liveChaptersRef = useRef<readonly ResolvedChapter[]>([]);
  useEffect(() => {
    liveGapsRef.current = liveGaps;
    liveChaptersRef.current = liveChapters;
  }, [liveGaps, liveChapters]);

  const onGapAction = useCallback(
    (gapKey: string, action: GapAction) => {
      if (!map) return;
      const chapterIdx = currentChapterIdxRef.current;
      const chapter = liveChaptersRef.current[chapterIdx];
      if (!chapter) return;
      const gap = parseGapKey(gapKey, liveGapsRef.current);
      if (!gap) return;
      const nextChapter = applyGapAction(chapter, gap, action, map.source_text);
      if (nextChapter) onChapterChange(nextChapter);
    },
    [map, onChapterChange],
  );

  const onSectionAction = useCallback(
    (subIdx: number, action: SectionAction) => {
      const chapterIdx = currentChapterIdxRef.current;
      const chapter = liveChaptersRef.current[chapterIdx];
      if (!chapter) return;
      const nextChapter = applySectionAction(chapter, subIdx, action);
      if (nextChapter) onChapterChange(nextChapter);
    },
    [onChapterChange],
  );

  // Reload the map from the server — used after Save / Approve / Reject
  // / Re-propose to pick up fresh server-computed offsets + status.
  const reloadMap = useCallback(async () => {
    if (!sourceDocId) return;
    const res = await getSourceMap(sourceDocId);
    if (res.ok) {
      setMap(res.data.map);
      setChapters(res.data.map.chapters);
      setBaselineChapters(res.data.map.chapters);
      setActionError(null);
      // MR 10 — fresh baseline means the pre-save undo history is no
      // longer coherent (undoing past Save would re-dirty the newly
      // persisted map). Clear both stacks.
      setUndoStack([]);
      setRedoStack([]);
    } else {
      setActionError(res.message ?? 'Failed to reload map.');
    }
  }, [sourceDocId]);

  const onSave = useCallback(async () => {
    if (!map || saving) return false;
    setSaving(true);
    setActionError(null);
    // Strip resolved-only fields (char_start, char_end) from chapters
    // + sub-sections — the PATCH endpoint expects the base BookChapterDef
    // shape. `excluded` on sub-sections IS persisted (added to the
    // validator in MR 2).
    const strippedChapters = chapters.map(stripResolvedChapter);
    const n = countAllEdits(chapters, baselineChapters);
    const res = await patchBookStructure(map.id, {
      chapters: strippedChapters,
    });
    if (res.ok) {
      await reloadMap();
      if (n > 0) {
        // Save success — undo would just un-save, which the reviewer
        // can achieve by making a new edit; no inline UNDO needed.
        emitToast(t('mapReview.toastSavedTemplate').replace('{n}', String(n)), {
          undoable: false,
        });
      }
      setSaving(false);
      return true;
    }
    setActionError(res.message ?? 'Failed to save.');
    setSaving(false);
    return false;
  }, [map, chapters, baselineChapters, saving, reloadMap, emitToast, t]);

  const onApprove = useCallback(async () => {
    if (!map || approving) return;
    // MR 6: save-first-if-dirty rather than blocking approve when dirty.
    // Matches the designer's spec §6: "Approve with unsaved edits does
    // NOT block — it saves first, and says so."
    if (areChaptersDirty(chapters, baselineChapters)) {
      const savedOk = await onSave();
      if (!savedOk) return; // save error already surfaced; don't approve
    }
    setApproving(true);
    setActionError(null);
    const res = await approveBookStructure(map.id);
    if (res.ok) {
      await reloadMap();
    } else {
      setActionError(res.message ?? 'Failed to approve.');
    }
    setApproving(false);
  }, [map, approving, chapters, baselineChapters, onSave, reloadMap]);

  // MR 6 — Discard all: reset chapters to their last-saved baseline.
  // Focused state may point at a section that no longer exists after
  // reset (e.g. NEW subs disappear); SourceDocPane defaults to the
  // first sub when the focused target is out of bounds, so no
  // separate reset needed here.
  const onOpenDiscardModal = useCallback(() => setDiscardModalOpen(true), []);
  const onCloseDiscardModal = useCallback(() => setDiscardModalOpen(false), []);
  const onConfirmDiscard = useCallback(() => {
    setChapters(baselineChapters);
    // MR 10 — discard also throws away the undo/redo history: we've
    // reset to baseline, so undo would move further BACKWARD into an
    // incoherent past. Fresh history from here.
    setUndoStack([]);
    setRedoStack([]);
    setDiscardModalOpen(false);
  }, [baselineChapters]);

  const onOpenRejectModal = useCallback(() => {
    setRejectReason('');
    setRejectModalOpen(true);
  }, []);
  const onCloseRejectModal = useCallback(() => {
    setRejectModalOpen(false);
    setRejectReason('');
  }, []);
  const onConfirmReject = useCallback(async () => {
    if (!map || rejecting) return;
    setRejecting(true);
    setActionError(null);
    const trimmed = rejectReason.trim();
    const res = await rejectBookStructure(
      map.id,
      trimmed.length > 0 ? { reason: trimmed } : {},
    );
    if (res.ok) {
      setRejectModalOpen(false);
      setRejectReason('');
      await reloadMap();
    } else {
      setActionError(res.message ?? 'Failed to reject.');
    }
    setRejecting(false);
  }, [map, rejectReason, rejecting, reloadMap]);

  const onRepropose = useCallback(async () => {
    if (!sourceDocId || reproposing) return;
    setReproposing(true);
    setActionError(null);
    const res = await proposeMap(sourceDocId);
    if (res.ok) {
      await reloadMap();
    } else {
      setActionError(res.message ?? 'Failed to re-propose.');
    }
    setReproposing(false);
  }, [sourceDocId, reproposing, reloadMap]);

  // MR 7 + MR 8 — selection-toolbar actions.
  //
  // Boundary-tier (MR 7): split-here, move-to-prev, move-to-next —
  // compile down to inserting a new sub / shifting a neighbour's
  // header_pattern. Paragraph scope.
  //
  // Paragraph-content-tier (MR 8 — enabled by MR 7b's data model):
  // rewrite-paragraph, insert-after-paragraph, exclude-paragraph —
  // compile down to appends/updates to the current sub's
  // paragraph_overrides array (shared type ParagraphOverride).
  //
  // The current sub is derived from `focused` the same way SourceDocPane
  // does it (falls back to first sub when focused is null / points at
  // the chapter itself).
  const onSelectionAction = useCallback(
    (action: SelectionAction) => {
      if (!map) return;
      const chapterIdx = currentChapterIdxRef.current;
      const chapter = liveChaptersRef.current[chapterIdx];
      if (!chapter || chapter.char_start === null) return;
      const explicit =
        focused && typeof focused.target === 'number' ? focused.target : 0;
      const currentSubIdx = Math.max(
        0,
        Math.min(chapter.sub_sections.length - 1, explicit),
      );
      let next: ResolvedChapter | null = null;
      let toastMessage: string | null = null;
      if (action.kind === 'split-range') {
        next = applySplitRangeFromSelection(
          chapter,
          currentSubIdx,
          action.startSeed,
          action.endSeed,
          action.startLabel,
          action.endLabel,
        );
        if (next) {
          // Move focus to the first newly-inserted sub (the middle
          // section when startSeed is non-null, otherwise the suffix).
          // Without this the right pane stays on the shrunken parent
          // "prefix" sub and the reviewer has no visual confirmation
          // that their new section actually appeared.
          setFocused({ target: currentSubIdx + 1 });
          // 2026-10-26 toast — references the PARENT section (§1.1) and
          // says how many sections it's now split into, rather than
          // naming the new §1.2. Matches the mock's "1.1 split into
          // two sections" / "1.1 carved into three sections".
          const parentRef = `§${chapter.number}.${currentSubIdx + 1}`;
          const key =
            action.startSeed && action.endSeed
              ? 'mapReview.splitToastCarve'
              : 'mapReview.splitToast';
          toastMessage = t(key).replace('{parent}', parentRef);
        }
      } else if (action.kind === 'move-to-next') {
        next = applyMoveToNeighbourFromSelection(
          chapter,
          currentSubIdx,
          action.startParaIdx,
          action.endParaIdx,
          map.source_text,
          'next',
        );
        if (next) {
          toastMessage = t('mapReview.toastMoveTemplate').replace(
            '{ref}',
            `§${chapter.number}.${currentSubIdx + 2}`,
          );
        }
      } else if (action.kind === 'move-to-prev') {
        next = applyMoveToNeighbourFromSelection(
          chapter,
          currentSubIdx,
          action.startParaIdx,
          action.endParaIdx,
          map.source_text,
          'prev',
        );
        if (next) {
          toastMessage = t('mapReview.toastMoveTemplate').replace(
            '{ref}',
            `§${chapter.number}.${currentSubIdx}`,
          );
        }
      } else if (action.kind === 'rewrite-paragraph') {
        next = applyParagraphOverride(chapter, currentSubIdx, {
          kind: 'rewrite',
          paragraph_index: action.paragraphIdx,
          replacement_text: action.replacementText,
          original_text: action.originalText,
        });
        if (next) {
          toastMessage = t('mapReview.toastRewriteTemplate').replace(
            '{ref}',
            `§${chapter.number}.${currentSubIdx + 1}`,
          );
        }
      } else if (action.kind === 'insert-after-paragraph') {
        next = applyParagraphOverride(chapter, currentSubIdx, {
          kind: 'insert_after',
          paragraph_index: action.paragraphIdx,
          inserted_text: action.insertedText,
        });
        if (next) {
          toastMessage = t('mapReview.toastInsertTemplate').replace(
            '{ref}',
            `§${chapter.number}.${currentSubIdx + 1}`,
          );
        }
      } else if (action.kind === 'exclude-paragraph') {
        next = applyParagraphOverride(chapter, currentSubIdx, {
          kind: 'exclude',
          paragraph_index: action.paragraphIdx,
        });
        if (next) {
          toastMessage = t('mapReview.toastExcludeParagraphTemplate').replace(
            '{ref}',
            `§${chapter.number}.${currentSubIdx + 1}`,
          );
        }
      }
      if (next) {
        onChapterChange(next);
        // Clear the browser selection so the toolbar dismisses + the
        // preview line disappears after commit.
        window.getSelection()?.removeAllRanges();
        if (toastMessage) emitToast(toastMessage);
      }
    },
    [map, focused, onChapterChange, emitToast, t],
  );

  // MR 8 — section-tier row cluster actions. Split from the row is a
  // navigation gesture (focus the row + wait for the reviewer to select
  // text in the right pane and hit `S`), so there's no reducer here —
  // onFocus handles it. Merge up actually mutates: the current sub's
  // paragraphs are absorbed into the previous sub by deleting the
  // current one; the previous sub's header_pattern is unchanged, its
  // char_end extends on next resolve.
  const onSectionMergeUp = useCallback(
    (subIdx: number) => {
      const chapterIdx = currentChapterIdxRef.current;
      const chapter = liveChaptersRef.current[chapterIdx];
      if (!chapter || subIdx <= 0 || subIdx >= chapter.sub_sections.length) return;
      const nextChapter: ResolvedChapter = {
        ...chapter,
        sub_sections: chapter.sub_sections.filter((_, i) => i !== subIdx),
      };
      onChapterChange(nextChapter);
      // Focus moves to the (now absorbed) previous sub.
      setFocused({ target: subIdx - 1 });
      emitToast(
        t('mapReview.toastMergeUpTemplate').replace(
          '{ref}',
          `§${chapter.number}.${subIdx}`,
        ),
      );
    },
    [onChapterChange, emitToast, t],
  );

  const onDividerChange = useCallback((frac: number) => {
    const clamped = clampDivider(frac);
    setDividerFrac(clamped);
    try {
      localStorage.setItem(DIVIDER_STORAGE_KEY, String(clamped));
    } catch {
      // Ignore — private-browsing or storage-full. Not worth blocking on.
    }
  }, []);

  // MR 6 — count of pending edits vs the last-saved baseline. Feeds the
  // unsaved-changes bar's chip + the discard modal + the footer's
  // "Approving saves your N edits first" note. Recomputed on every
  // chapters / baseline change; O(chapters * subs) which is fine.
  const editCount = useMemo(
    () => countAllEdits(chapters, baselineChapters),
    [chapters, baselineChapters],
  );

  // MR 6 — global ⌘S / Ctrl+S shortcut. Saves when dirty; no-op when
  // clean, when a save is already in flight, or when the map is
  // read-only. Fires from anywhere on the page (including inside inputs
  // — Save is unambiguous). preventDefault to swallow the browser's own
  // "save page" behavior.
  //
  // MR 10 — same handler also owns ⌘Z (undo) and ⇧⌘Z (redo). Both pop
  // from their respective stacks via the callbacks defined above; both
  // no-op silently when their stack is empty so the reviewer can mash
  // the shortcut without unexpected side-effects.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.metaKey && !e.ctrlKey) return;
      const key = e.key.toLowerCase();
      if (key === 's') {
        e.preventDefault();
        if (editCount === 0 || saving || approving) return;
        void onSave();
        return;
      }
      if (key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
        return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editCount, saving, approving, onSave, undo, redo]);

  // MR 6 — toast auto-dismiss (6s per spec §7). Any subsequent setToast
  // call resets the clock: the newest message wins, the older one just
  // disappears.
  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), 6000);
    return () => window.clearTimeout(id);
  }, [toast]);

  // ─── Loading state ─────────────────────────────────────────────────
  if (loading) {
    return (
      <AdminShell activeKey="sources" breadcrumb={[]} state={{ kind: 'loading' }}>
        <PageHeader title={t('mapReview.loadingTitle')} sub={t('mapReview.loadingSub')} />
      </AdminShell>
    );
  }

  // ─── Error state ───────────────────────────────────────────────────
  if (loadError || !map) {
    return (
      <AdminShell activeKey="sources" breadcrumb={[]} state={{ kind: 'normal' }}>
        <PageHeader title={t('mapReview.loadErrorTitle')} />
        <Alert severity="danger">{loadError ?? t('mapReview.loadErrorGeneric')}</Alert>
      </AdminShell>
    );
  }

  // ─── Still proposing ───────────────────────────────────────────────
  if (map.proposing) {
    return (
      <AdminShell activeKey="sources" breadcrumb={[]} state={{ kind: 'normal' }}>
        <PageHeader title={map.name} sub={t('mapReview.proposingSub')} />
        <div className={styles.centeredSpinner}>
          <Spinner />
        </div>
      </AdminShell>
    );
  }

  // ─── Ready — render the shell ──────────────────────────────────────
  // liveChapters is the client-side re-resolved copy of chapters — the
  // right pane paints its spans from these offsets, the tree reads
  // them for the amber NO-MATCH pill state, etc.
  const totalChapters = liveTokenChapters.length;
  // Clamp the index defensively — if chapters ever shrinks below the
  // current index (e.g. an entire chapter removed), snap to the last.
  const safeIdx = Math.min(currentChapterIdx, totalChapters - 1);
  const currentChapter = liveTokenChapters[safeIdx];
  const chapterTitle = currentChapter?.title ?? map.name;
  const chapterNumber = currentChapter?.number ?? safeIdx + 1;
  const wordCount = countWordsIn(map.source_text);
  const estimatedTokens = currentChapter?.estimated_tokens ?? 0;
  const readOnly = map.status === 'approved';

  return (
    <AdminShell
      activeKey="sources"
      breadcrumb={[
        t('mapReview.breadcrumbKnowledge'),
        t('mapReview.breadcrumbSources'),
        t('mapReview.breadcrumbMapReview'),
      ]}
      state={{ kind: 'normal' }}
    >
      <PageHeader
        title={chapterTitle}
        sub={buildMetaLine(t, {
          docId: shortDocId(map.source_document_id),
          chapterNumber,
          totalChapters,
          language: map.language.toUpperCase(),
          wordCount,
          tokens: estimatedTokens,
        })}
        actions={
          <>
            <HistoryToggleButton
              open={historyOpen}
              onToggle={() => setHistoryOpen((v) => !v)}
              t={t}
            />
            <StatusPill status={map.status} t={t} />
          </>
        }
      />

      {editCount > 0 && map.status !== 'approved' && (
        <UnsavedChangesBar
          count={editCount}
          saving={saving}
          onSave={() => void onSave()}
          onDiscard={onOpenDiscardModal}
          t={t}
        />
      )}

      <MapReviewLayout
        dividerFrac={dividerFrac}
        onDividerChange={onDividerChange}
        leftPane={
          currentChapter ? (
            <MapTree
              chapter={currentChapter}
              {...(baselineChapters[chapterNumber - 1]
                ? { baselineChapter: baselineChapters[chapterNumber - 1]! }
                : {})}
              chapterNumber={chapterNumber}
              totalChapters={totalChapters}
              focused={focused}
              readOnly={readOnly}
              onChange={onChapterChange}
              onFocus={setFocused}
              onPrevChapter={onPrevChapter}
              onNextChapter={onNextChapter}
              t={t}
            />
          ) : (
            <LeftPanePlaceholder t={t} />
          )
        }
        rightPane={
          currentChapter ? (
            <SourceDocPane
              sourceText={map.source_text}
              chapter={currentChapter}
              {...(baselineChapters[safeIdx]
                ? { baselineChapter: baselineChapters[safeIdx]! }
                : {})}
              chapterNumber={chapterNumber}
              gaps={liveGaps}
              focused={focused}
              onSectionMergeUp={onSectionMergeUp}
              readOnly={readOnly}
              onGapAction={onGapAction}
              onSectionAction={onSectionAction}
              onSelectionAction={onSelectionAction}
              t={t}
            />
          ) : (
            <RightPanePlaceholder t={t} />
          )
        }
      />

      {actionError && (
        <Alert severity="danger" onDismiss={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      <MapReviewFooter
        status={map.status}
        editCount={editCount}
        saving={saving}
        approving={approving}
        reproposing={reproposing}
        rejectedReason={map.rejected_reason}
        onSave={() => void onSave()}
        onApprove={onApprove}
        onOpenReject={onOpenRejectModal}
        onRepropose={onRepropose}
        t={t}
      />

      {rejectModalOpen && (
        <RejectModal
          reason={rejectReason}
          onReasonChange={setRejectReason}
          onCancel={onCloseRejectModal}
          onConfirm={onConfirmReject}
          submitting={rejecting}
          t={t}
        />
      )}

      {discardModalOpen && (
        <DiscardModal
          count={editCount}
          onCancel={onCloseDiscardModal}
          onConfirm={onConfirmDiscard}
          t={t}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          canUndo={toast.undoable && undoStack.length > 0}
          onUndo={undo}
          onDismiss={() => setToast(null)}
          undoLabel={t('mapReview.toastUndoLabel')}
          undoShortcutLabel={t('mapReview.toastUndoShortcut')}
        />
      )}

      {historyOpen && (
        <HistoryDrawer
          structureId={map.id}
          onClose={() => setHistoryOpen(false)}
          t={t}
        />
      )}
    </AdminShell>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────

interface MapReviewLayoutProps {
  dividerFrac: number;
  onDividerChange: (frac: number) => void;
  leftPane: React.ReactNode;
  rightPane: React.ReactNode;
}

/**
 * Two-pane grid with a draggable vertical divider between them. The
 * divider's position is expressed as a fraction of the container width
 * (0..1) and persisted per-user in localStorage. Drag-with-pointer
 * events (not mousedown/mouseup) so touch + pen work automatically.
 */
function MapReviewLayout(p: MapReviewLayoutProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      draggingRef.current = true;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const frac = (e.clientX - rect.left) / rect.width;
      p.onDividerChange(frac);
    },
    [p],
  );

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    draggingRef.current = false;
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
  }, []);

  return (
    <div
      ref={containerRef}
      className={styles.layout}
      style={{ gridTemplateColumns: `${p.dividerFrac * 100}% 6px 1fr` }}
    >
      <div className={styles.pane}>{p.leftPane}</div>
      <div
        className={styles.divider}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize panes"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      />
      <div className={styles.pane}>{p.rightPane}</div>
    </div>
  );
}

interface MapReviewFooterProps {
  status: ResolvedBookStructure['status'];
  /** Number of pending edits vs the last-saved baseline. Zero = clean. */
  editCount: number;
  saving: boolean;
  approving: boolean;
  reproposing: boolean;
  rejectedReason: string | null;
  onSave: () => void;
  onApprove: () => void;
  onOpenReject: () => void;
  onRepropose: () => void;
  t: (k: string) => string;
}

function MapReviewFooter(p: MapReviewFooterProps) {
  const readOnly = p.status === 'approved';
  const rejected = p.status === 'rejected';

  if (rejected) {
    // Rejected state: previous reject-reason surfaced inline, and the
    // reviewer's only action is Re-propose (which triggers a fresh
    // Sonnet pass on the source doc).
    return (
      <footer className={styles.footer}>
        <div className={styles.footerLeft}>
          <span className={styles.footerRejectedLabel}>
            {p.t('mapReview.footerRejected')}
          </span>
          {p.rejectedReason && (
            <span className={styles.footerRejectedReason}>
              &ldquo;{p.rejectedReason}&rdquo;
            </span>
          )}
        </div>
        <div className={styles.footerActions}>
          <Button kind="primary" onClick={p.onRepropose} disabled={p.reproposing}>
            {p.reproposing
              ? p.t('mapReview.footerReproposing')
              : p.t('mapReview.footerRepropose')}
          </Button>
        </div>
      </footer>
    );
  }

  const dirty = p.editCount > 0;
  return (
    <footer className={styles.footer}>
      <div className={styles.footerLeft}>
        {readOnly && (
          <span className={styles.footerReadOnlyLabel}>
            {p.t('mapReview.footerReadOnly')}
          </span>
        )}
        {!readOnly && dirty && (
          <span className={styles.footerApproveWithUnsavedNote}>
            {p.t('mapReview.footerApproveWithUnsavedTemplate').replace(
              '{n}',
              String(p.editCount),
            )}
          </span>
        )}
      </div>
      <div className={styles.footerActions}>
        <Button
          kind="ghost"
          onClick={p.onOpenReject}
          disabled={readOnly || p.saving || p.approving}
        >
          {p.t('mapReview.footerReject')}
        </Button>
        <Button
          kind="ghost"
          onClick={p.onSave}
          disabled={readOnly || !dirty || p.saving || p.approving}
        >
          {p.saving ? p.t('mapReview.footerSaving') : p.t('mapReview.footerSave')}
        </Button>
        <Button
          kind="primary"
          onClick={p.onApprove}
          disabled={readOnly || p.saving || p.approving}
        >
          {p.approving
            ? p.t('mapReview.footerApproving')
            : p.t('mapReview.footerApprove')}
        </Button>
      </div>
    </footer>
  );
}

// ─── RejectModal ─────────────────────────────────────────────────────
//
// Inline modal (backdrop + centered card) for capturing a rejection
// reason. Reason is optional per the API contract, but the UX
// encourages one — a rejected map without a reason gives future
// reviewers no signal about WHY the AI's proposal wasn't accepted.

interface RejectModalProps {
  reason: string;
  onReasonChange: (next: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  submitting: boolean;
  t: (k: string) => string;
}

function RejectModal(p: RejectModalProps) {
  // Escape key closes (unless in-flight). Also: clicking the backdrop
  // (not the card itself) cancels. Both are keyboard/pointer parity
  // with what a screen-reader user expects from a modal dialog.
  useEffect(() => {
    if (p.submitting) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') p.onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [p]);

  return (
    <div
      className={styles.modalBackdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="reject-modal-title"
      onClick={(e) => {
        // Only cancel when the backdrop itself is clicked — not when
        // a click bubbles up from inside the card.
        if (e.target === e.currentTarget && !p.submitting) p.onCancel();
      }}
    >
      <div className={styles.modalCard}>
        <h3 id="reject-modal-title" className={styles.modalTitle}>
          {p.t('mapReview.rejectModalTitle')}
        </h3>
        <p className={styles.modalBody}>{p.t('mapReview.rejectModalBody')}</p>
        <textarea
          className={styles.modalTextarea}
          value={p.reason}
          onChange={(e) => p.onReasonChange(e.target.value)}
          placeholder={p.t('mapReview.rejectModalReasonPlaceholder')}
          rows={4}
          disabled={p.submitting}
          autoFocus
        />
        <div className={styles.modalActions}>
          <Button kind="ghost" onClick={p.onCancel} disabled={p.submitting}>
            {p.t('mapReview.rejectModalCancel')}
          </Button>
          <Button
            kind="primary"
            onClick={p.onConfirm}
            disabled={p.submitting}
          >
            {p.submitting
              ? p.t('mapReview.rejectModalConfirming')
              : p.t('mapReview.rejectModalConfirm')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── UnsavedChangesBar (MR 6) ────────────────────────────────────────
//
// Dark sticky-ish bar that appears between the doc header and the panes
// when there are pending edits. Communicates the count + gives the
// reviewer immediate Save / Discard affordances without having to hunt
// down the footer. Approving from the footer while this bar is visible
// auto-saves first (see onApprove in the parent).

function UnsavedChangesBar({
  count,
  saving,
  onSave,
  onDiscard,
  t,
}: {
  count: number;
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
  t: (k: string) => string;
}) {
  return (
    <div className={styles.unsavedBar} role="status" aria-live="polite">
      <span className={styles.unsavedBarChip}>
        {t('mapReview.unsavedBarCountTemplate').replace('{n}', String(count))}
      </span>
      <span className={styles.unsavedBarDescription}>
        {t('mapReview.unsavedBarDescription')}
      </span>
      <span className={styles.unsavedBarSpacer} />
      <button
        type="button"
        className={styles.unsavedBarDiscardButton}
        onClick={onDiscard}
        disabled={saving}
      >
        {t('mapReview.unsavedBarDiscardAll')}
      </button>
      <button
        type="button"
        className={styles.unsavedBarSaveButton}
        onClick={onSave}
        disabled={saving}
      >
        {t('mapReview.unsavedBarSave')}
        <span className={styles.unsavedBarShortcut}>
          {t('mapReview.unsavedBarSaveShortcut')}
        </span>
      </button>
    </div>
  );
}

// ─── DiscardModal (MR 6) ─────────────────────────────────────────────
//
// Confirm dialog for "Discard all". Same backdrop/card/Escape pattern
// as RejectModal — this one just doesn't need a text field. Called out
// as its own component so the copy + count interpolation live near the
// bar that triggers it.

function DiscardModal({
  count,
  onCancel,
  onConfirm,
  t,
}: {
  count: number;
  onCancel: () => void;
  onConfirm: () => void;
  t: (k: string) => string;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      className={styles.modalBackdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="discard-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className={styles.modalCard}>
        <h3 id="discard-modal-title" className={styles.modalTitle}>
          {t('mapReview.discardModalTitleTemplate').replace('{n}', String(count))}
        </h3>
        <p className={styles.modalBody}>{t('mapReview.discardModalBody')}</p>
        <div className={styles.modalActions}>
          <Button kind="ghost" onClick={onCancel}>
            {t('mapReview.discardModalCancel')}
          </Button>
          <Button kind="primary" onClick={onConfirm}>
            {t('mapReview.discardModalConfirm')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Toast (MR 6) ────────────────────────────────────────────────────
//
// Minimal dark toast, fixed at the bottom-center of the viewport.
// MR 6 fires it on save-success; MR 10 will add "inline undo" variants
// wired to the per-doc undo stack for section actions (split, merge,
// exclude, delete). Auto-dismiss timing lives at the parent (6s).

function Toast({
  message,
  canUndo,
  onUndo,
  onDismiss,
  undoLabel,
  undoShortcutLabel,
}: {
  message: string;
  /** True when there's actually something on the undo stack AND the
   *  caller marked this toast as undoable (save-success toasts set
   *  false). When false, the UNDO affordance is hidden. */
  canUndo: boolean;
  onUndo: () => void;
  onDismiss: () => void;
  undoLabel: string;
  undoShortcutLabel: string;
}) {
  return (
    <div className={styles.toast} role="status" aria-live="polite">
      <span className={styles.toastMessage} onClick={onDismiss}>
        {message}
      </span>
      {canUndo && (
        <button
          type="button"
          className={styles.toastUndo}
          onClick={(e) => {
            e.stopPropagation();
            onUndo();
          }}
        >
          {undoLabel}
          <kbd className={styles.toastUndoKbd}>{undoShortcutLabel}</kbd>
        </button>
      )}
    </div>
  );
}

// ─── HistoryToggleButton + HistoryDrawer (MR 10) ────────────────────────

/** Small toggle sitting in the page header's actions slot. Opens the
 *  right-side history drawer. Design mirrors the mock's `.ch.hh` toggle
 *  (edit-history pill) — monospace label, subtle border. */
function HistoryToggleButton({
  open,
  onToggle,
  t,
}: {
  open: boolean;
  onToggle: () => void;
  t: (k: string) => string;
}) {
  return (
    <button
      type="button"
      className={open ? styles.historyToggleActive : styles.historyToggle}
      onClick={onToggle}
      aria-pressed={open ? 'true' : 'false'}
      aria-label={t('mapReview.historyToggleAria')}
    >
      {t('mapReview.historyToggleLabel')}
    </button>
  );
}

/** Right-side drawer that lists mutation audit entries for the current
 *  BookStructure, newest first. Fetches on first open; the fetched list
 *  stays in state so toggling close/open is instant. A refresh after
 *  Save isn't automatic (the Toast + unsaved bar already surface recent
 *  activity) — the reviewer closes + re-opens the drawer to pull the
 *  latest if they want to see the Save row. */
function HistoryDrawer({
  structureId,
  onClose,
  t,
}: {
  structureId: string;
  onClose: () => void;
  t: (k: string) => string;
}) {
  const [entries, setEntries] = useState<BookStructureAuditEntry[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    void (async () => {
      const res = await listBookStructureAudit(structureId);
      if (cancelled) return;
      setLoading(false);
      if (res.ok) setEntries(res.data.entries);
      else setLoadError(res.message ?? 'Failed to load history.');
    })();
    return () => {
      cancelled = true;
    };
  }, [structureId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className={styles.historyDrawerBackdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <aside
        className={styles.historyDrawer}
        role="dialog"
        aria-modal="true"
        aria-labelledby="history-drawer-title"
      >
        <header className={styles.historyDrawerHeader}>
          <h3 id="history-drawer-title" className={styles.historyDrawerTitle}>
            {t('mapReview.historyDrawerTitle')}
          </h3>
          <button
            type="button"
            className={styles.historyDrawerClose}
            onClick={onClose}
            aria-label={t('mapReview.historyDrawerCloseAria')}
          >
            ✕
          </button>
        </header>
        <div className={styles.historyDrawerBody}>
          {loading && (
            <p className={styles.historyDrawerEmpty}>
              {t('mapReview.historyDrawerLoading')}
            </p>
          )}
          {loadError && !loading && (
            <Alert severity="danger">{loadError}</Alert>
          )}
          {!loading && !loadError && entries && entries.length === 0 && (
            <p className={styles.historyDrawerEmpty}>
              {t('mapReview.historyDrawerEmpty')}
            </p>
          )}
          {!loading && !loadError && entries && entries.length > 0 && (
            <ul className={styles.historyList}>
              {entries.map((e) => (
                <HistoryEntry key={e.id} entry={e} t={t} />
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}

function HistoryEntry({
  entry,
  t,
}: {
  entry: BookStructureAuditEntry;
  t: (k: string) => string;
}) {
  const opLabel = t(`mapReview.historyOp_${entry.op.replace('-', '_')}`);
  const actor = entry.actor_sub.startsWith('system:')
    ? t(`mapReview.historyActor_${entry.actor_sub.replace(/[:-]/g, '_')}`)
    : entry.actor_sub.slice(0, 8);
  const when = new Date(entry.created_at);
  const whenLabel = when.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  return (
    <li className={styles.historyEntry}>
      <div className={styles.historyEntryRow}>
        <span className={styles.historyEntryOp}>{opLabel}</span>
        <span className={styles.historyEntryVersion}>
          v{entry.from_version} → v{entry.to_version}
        </span>
      </div>
      <div className={styles.historyEntryMeta}>
        <span>{whenLabel}</span>
        <span className={styles.historyEntryActor}>{actor}</span>
      </div>
      {entry.reason && (
        <p className={styles.historyEntryReason}>&ldquo;{entry.reason}&rdquo;</p>
      )}
    </li>
  );
}

interface StatusPillProps {
  status: ResolvedBookStructure['status'];
  t: (k: string) => string;
}

function StatusPill(p: StatusPillProps) {
  const className =
    p.status === 'approved'
      ? styles.statusPillApproved
      : p.status === 'rejected'
        ? styles.statusPillRejected
        : styles.statusPillAwaiting;
  const label =
    p.status === 'approved'
      ? p.t('mapReview.statusApproved')
      : p.status === 'rejected'
        ? p.t('mapReview.statusRejected')
        : p.t('mapReview.statusAwaiting');
  return <span className={`${styles.statusPill} ${className}`}>{label}</span>;
}

// Placeholders — MR 4 replaces LeftPanePlaceholder with <MapTree>,
// MR 5 replaces RightPanePlaceholder with <SourceDocPane>.

function LeftPanePlaceholder({ t }: { t: (k: string) => string }) {
  return (
    <div className={styles.placeholder}>
      <div className={styles.placeholderLabel}>{t('mapReview.leftPanePlaceholderTitle')}</div>
      <div className={styles.placeholderBody}>{t('mapReview.leftPanePlaceholderBody')}</div>
    </div>
  );
}

function RightPanePlaceholder({ t }: { t: (k: string) => string }) {
  return (
    <div className={styles.placeholder}>
      <div className={styles.placeholderLabel}>{t('mapReview.rightPanePlaceholderTitle')}</div>
      <div className={styles.placeholderBody}>{t('mapReview.rightPanePlaceholderBody')}</div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function readStoredDivider(): number {
  try {
    const raw = localStorage.getItem(DIVIDER_STORAGE_KEY);
    if (!raw) return DIVIDER_DEFAULT;
    const parsed = parseFloat(raw);
    if (!Number.isFinite(parsed)) return DIVIDER_DEFAULT;
    return clampDivider(parsed);
  } catch {
    return DIVIDER_DEFAULT;
  }
}

function clampDivider(frac: number): number {
  return Math.max(DIVIDER_MIN, Math.min(DIVIDER_MAX, frac));
}

/**
 * Build the doc-header meta line from the spec:
 *   "{docId} · CHAPTER {n} OF {total} · {LANG} · {words} WORDS · ~{tok} TOK"
 * FR: same with locale-formatted numbers (11 257 vs 11,257). The i18n
 * layer handles the WORDS / MOTS + TOK swap.
 */
function buildMetaLine(
  t: (k: string) => string,
  parts: {
    docId: string;
    chapterNumber: number;
    totalChapters: number;
    language: string;
    wordCount: number;
    tokens: number;
  },
): string {
  return [
    parts.docId,
    `${t('mapReview.metaChapterLabel')} ${parts.chapterNumber} ${t('mapReview.metaOf')} ${parts.totalChapters}`,
    parts.language,
    `${parts.wordCount.toLocaleString()} ${t('mapReview.metaWords')}`,
    `~${parts.tokens.toLocaleString()} ${t('mapReview.metaTokens')}`,
  ].join(' · ');
}

function shortDocId(id: string): string {
  return `DOC-${id.slice(-4).toUpperCase()}`;
}

function countWordsIn(text: string): number {
  const trimmed = text.trim();
  if (trimmed.length === 0) return 0;
  return trimmed.split(/\s+/).length;
}

// ─── Gap + section action helpers ─────────────────────────────────────
//
// Mutations produce a new ResolvedChapter. char_start/char_end on
// newly-created or shifted sections are set to null; the doc pane
// paints them absent until Save round-trips through GET /map. This
// matches the "no client-side re-resolution" decision from MR 4.

/** Decode a "start-end" gap key back to the actual MapGap object.
 *  Returns null if the key doesn't match any current gap (e.g. the
 *  gap disappeared after another edit). */
function parseGapKey(
  key: string,
  gaps: readonly MapGap[],
): MapGap | null {
  const [startStr, endStr] = key.split('-');
  if (!startStr || !endStr) return null;
  const start = parseInt(startStr, 10);
  const end = parseInt(endStr, 10);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return gaps.find((g) => g.char_start === start && g.char_end === end) ?? null;
}

function applyGapAction(
  chapter: ResolvedChapter,
  gap: MapGap,
  action: GapAction,
  sourceText: string,
): ResolvedChapter | null {
  const gapText = sourceText.slice(gap.char_start, gap.char_end);
  const derivedPattern = deriveHeaderPatternFromText(gapText);

  if (action === 'new-section-from-gap') {
    // Append a new sub-section with a derived label + pattern. The
    // reviewer can rename + tune in the tree before Save.
    const newSub: ResolvedSubSection = {
      label: derivedPattern || 'New sub-section',
      header_pattern: derivedPattern,
      char_start: null,
      char_end: null,
    };
    return { ...chapter, sub_sections: [...chapter.sub_sections, newSub] };
  }

  if (action === 'mark-intentional-gap') {
    // Same as new-section-from-gap but flagged excluded so the chunker
    // skips it. Explicitly claims the gap without producing a KB entry.
    const newSub: ResolvedSubSection = {
      label: `[Intentional] ${derivedPattern || 'gap'}`,
      header_pattern: derivedPattern,
      char_start: null,
      char_end: null,
      excluded: true,
    };
    return { ...chapter, sub_sections: [...chapter.sub_sections, newSub] };
  }

  if (action === 'extend-previous-section') {
    // Find the sub-section whose char_end is at or just before the
    // gap's char_start. Extending = the gap's content will BE part of
    // that sub after re-resolution. Since we don't re-resolve client-
    // side, we can't visually show the extension until Save. We DO
    // signal intent: no chapter change needed — the neighbor's
    // header_pattern still matches at the same position, but on
    // re-resolve its char_end will extend forward to the next
    // resolved boundary (which is now past the gap).
    //
    // Practical effect for V1: the user's "Extend previous section"
    // click is a no-op on the chapter data (there's nothing to
    // change), but the "gap" itself vanishes on next Save if no other
    // section starts inside it. If a downstream section DOES start
    // inside the gap, extending previous doesn't help — the reviewer
    // wanted a merge, which requires deleting a boundary. Surface
    // "extend previous" ONLY when there's no sub-section starting
    // inside the gap. For MR 6, we take the honest path and no-op
    // silently; MR 8 can add richer merge logic + client-side re-
    // resolve if this feels weak in Olivier's testing.
    return null;
  }

  return null;
}

/**
 * MR 7 — Split a sub-section at a paragraph boundary.
 *
 * The reviewer selected text starting inside paragraph `paragraphIdx`
 * of the current sub. We insert a NEW sub-section right after the
 * current one; its `header_pattern` is derived from the first ~6 words
 * of that paragraph so the chunker will re-match the boundary at
 * ingest time. The new sub starts with an empty label — the reviewer
 * renames it inline on the left after the split.
 *
 * char_start / char_end stay null on the new sub; the server re-
 * resolves them from the pattern on the next reload. The current sub's
 * char_end effectively shrinks (once resolved) because its slice now
 * bounds at the new sub's start.
 *
 * Returns null when the split would produce an empty half — index 0 or
 * past the last paragraph. The toolbar disables the button in the
 * index-0 case; this is the belt-and-braces guard.
 */
/**
 * 2026-10-22 — Split a sub-section by a user-defined RANGE (option D
 * from the chat): the reviewer clicks to place the start, drags an
 * end bar to the end, and we carve the current sub into up to three
 * pieces: prefix (what's before start), middle (the carved range),
 * suffix (what's after end).
 *
 * Data-model shape: the parent sub stays as-is (its header_pattern
 * still anchors its origin); we insert up to two NEW subs after it:
 *   - middle sub: pattern derived from `startSeed` (text at start)
 *   - suffix sub: pattern derived from `endSeed`   (text at end)
 *
 * The chunker's "slice until next pattern match" semantics then
 * naturally produce:
 *   - current sub → [original origin → startSeed match]  (prefix)
 *   - middle sub  → [startSeed match → endSeed match]
 *   - suffix sub  → [endSeed match → next existing sub]
 *
 * Either seed may be null (start at origin → no prefix, no middle
 * needed; end at tail → no suffix). Both null → no-op.
 *
 * Pattern quality is the user's responsibility per Pierre on
 * 2026-10-22: "If the split makes no sense it is the responsibility
 * of the user." Mid-sentence patterns can collide elsewhere in the
 * chapter and resolve wrong at ingest; the UI will show NO MATCH on
 * save/reload if that happens, and the reviewer can fix the label +
 * pattern inline.
 */
function applySplitRangeFromSelection(
  chapter: ResolvedChapter,
  currentSubIdx: number,
  startSeed: string | null,
  endSeed: string | null,
  startLabel?: string,
  endLabel?: string,
): ResolvedChapter | null {
  const sub = chapter.sub_sections[currentSubIdx];
  if (!sub) return null;
  const inserts: ResolvedSubSection[] = [];
  if (startSeed) {
    const derived = deriveHeaderPatternFromText(startSeed);
    if (derived) {
      inserts.push({
        label: startLabel ?? '',
        header_pattern: derived,
        char_start: null,
        char_end: null,
      });
    }
  }
  if (endSeed) {
    const derived = deriveHeaderPatternFromText(endSeed);
    if (derived) {
      inserts.push({
        label: endLabel ?? '',
        header_pattern: derived,
        char_start: null,
        char_end: null,
      });
    }
  }
  if (inserts.length === 0) return null;
  return {
    ...chapter,
    sub_sections: [
      ...chapter.sub_sections.slice(0, currentSubIdx + 1),
      ...inserts,
      ...chapter.sub_sections.slice(currentSubIdx + 1),
    ],
  };
}

/**
 * MR 7 — Move a selected paragraph range from the current sub to its
 * previous or next neighbour.
 *
 * The current data model expresses sub-boundaries via header_pattern
 * matches, so a "move" is really "shift a neighbour's pattern to match
 * an earlier / later paragraph":
 *
 *   - Move to NEXT: next sub's pattern moves EARLIER — becomes the
 *     first moved paragraph. Its bounded slice starts higher up; the
 *     current sub effectively loses its tail.
 *
 *   - Move to PREV: current sub's pattern moves LATER — becomes the
 *     first paragraph AFTER the moved range. Prev sub's bounded slice
 *     now includes the moved range; current sub starts later.
 *
 * We refuse when the move would leave the current sub with zero
 * paragraphs (reviewer should Delete the section instead). char_start /
 * char_end reset to null on the mutated sub so the server re-resolves.
 */
function applyMoveToNeighbourFromSelection(
  chapter: ResolvedChapter,
  currentSubIdx: number,
  startParaIdx: number,
  endParaIdx: number,
  sourceText: string,
  direction: 'prev' | 'next',
): ResolvedChapter | null {
  const sub = chapter.sub_sections[currentSubIdx];
  const neighbourIdx =
    direction === 'next' ? currentSubIdx + 1 : currentSubIdx - 1;
  const neighbour = chapter.sub_sections[neighbourIdx];
  if (!sub || !neighbour || sub.char_start === null || sub.char_end === null) {
    return null;
  }
  const bodyText = sourceText.slice(sub.char_start, sub.char_end).trim();
  const paragraphs = bodyText
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  if (startParaIdx < 0 || endParaIdx >= paragraphs.length) return null;

  if (direction === 'next') {
    // Next sub's new pattern = first moved paragraph.
    const first = paragraphs[startParaIdx];
    if (!first) return null;
    const derived = deriveHeaderPatternFromText(first);
    if (!derived) return null;
    return {
      ...chapter,
      sub_sections: chapter.sub_sections.map((s, i) =>
        i === neighbourIdx
          ? { ...s, header_pattern: derived, char_start: null, char_end: null }
          : s,
      ),
    };
  }

  // direction === 'prev' — current sub's new pattern = first
  // paragraph AFTER the moved range. If nothing remains, refuse
  // (reviewer should Delete instead).
  const firstRemaining = paragraphs[endParaIdx + 1];
  if (!firstRemaining) return null;
  const derived = deriveHeaderPatternFromText(firstRemaining);
  if (!derived) return null;
  return {
    ...chapter,
    sub_sections: chapter.sub_sections.map((s, i) =>
      i === currentSubIdx
        ? { ...s, header_pattern: derived, char_start: null, char_end: null }
        : s,
    ),
  };
}

/** Strip resolved-only fields to produce the wire shape PATCH expects.
 *  char_start / char_end are read-time computations, not persisted;
 *  `excluded` + `paragraph_overrides` on sub-sections ARE persisted. */
function stripResolvedChapter(
  ch: ResolvedChapter,
): BookChapterDef {
  const base: BookChapterDef = {
    number: ch.number,
    title: ch.title,
    header_pattern: ch.header_pattern,
    sub_sections: ch.sub_sections.map((s) => {
      const sub: BookSubSectionDef = {
        label: s.label,
        header_pattern: s.header_pattern,
      };
      if (s.estimated_tokens !== undefined) sub.estimated_tokens = s.estimated_tokens;
      if (s.excluded !== undefined) sub.excluded = s.excluded;
      if (s.paragraph_overrides && s.paragraph_overrides.length > 0) {
        sub.paragraph_overrides = s.paragraph_overrides;
      }
      return sub;
    }),
  };
  if (ch.estimated_tokens !== undefined) base.estimated_tokens = ch.estimated_tokens;
  return base;
}

/** Shallow-structural dirty check between current + baseline chapters.
 *  Compares editable fields only (title, header_pattern, excluded,
 *  sub-section presence + order). Ignores char_start/char_end +
 *  estimated_tokens since those come from the server and never change
 *  as a result of user edits alone. */
function areChaptersDirty(
  current: readonly ResolvedChapter[],
  baseline: readonly ResolvedChapter[],
): boolean {
  if (current.length !== baseline.length) return true;
  for (let i = 0; i < current.length; i++) {
    const a = current[i]!;
    const b = baseline[i]!;
    if (a.title !== b.title || a.header_pattern !== b.header_pattern) return true;
    if (a.sub_sections.length !== b.sub_sections.length) return true;
    for (let j = 0; j < a.sub_sections.length; j++) {
      const as = a.sub_sections[j]!;
      const bs = b.sub_sections[j]!;
      if (
        as.label !== bs.label ||
        as.header_pattern !== bs.header_pattern ||
        (as.excluded ?? false) !== (bs.excluded ?? false) ||
        !areOverridesEqual(as.paragraph_overrides, bs.paragraph_overrides)
      ) {
        return true;
      }
    }
  }
  return false;
}

/** paragraph_overrides are compared by (kind, paragraph_index, text).
 *  Order-sensitive so we don't claim "clean" when the reviewer
 *  reordered equivalent inserts (which would silently change the
 *  rendered output). Empty + undefined are equivalent. */
function areOverridesEqual(
  a: readonly ParagraphOverride[] | undefined,
  b: readonly ParagraphOverride[] | undefined,
): boolean {
  const aa = a ?? [];
  const bb = b ?? [];
  if (aa.length !== bb.length) return false;
  for (let i = 0; i < aa.length; i += 1) {
    const x = aa[i]!;
    const y = bb[i]!;
    if (x.kind !== y.kind || x.paragraph_index !== y.paragraph_index) return false;
    if (x.kind === 'rewrite' && y.kind === 'rewrite') {
      if (x.replacement_text !== y.replacement_text) return false;
      if (x.original_text !== y.original_text) return false;
    } else if (x.kind === 'insert_after' && y.kind === 'insert_after') {
      if (x.inserted_text !== y.inserted_text) return false;
    }
    // 'exclude' has no extra fields.
  }
  return true;
}

/** Sum of pending edits across every chapter — how many discrete
 *  changes the reviewer has made since the last-saved baseline. Feeds
 *  the unsaved-changes bar chip + the discard modal + the footer's
 *  "Approving saves your N edits first" note. Same field set as
 *  areChaptersDirty; that helper is the boolean short-circuit, this
 *  one carries the count.
 *
 *  Counting rules:
 *   - Each sub-section whose editable fields differ from baseline = 1
 *   - Each sub-section added since baseline (index >= baseline.subs.length) = 1
 *   - Each sub-section removed since baseline = 1
 *   - Chapter meta differs (title or header_pattern) = 1 per chapter
 *   - Chapter added / removed (length differs) — each chapter delta = 1
 *   - Baseline empty (initial load) → 0 (nothing to compare against yet). */
function countAllEdits(
  current: readonly ResolvedChapter[],
  baseline: readonly ResolvedChapter[],
): number {
  if (baseline.length === 0) return 0;
  let count = 0;
  const chapterCountDelta = Math.abs(current.length - baseline.length);
  count += chapterCountDelta;
  const commonChapters = Math.min(current.length, baseline.length);
  for (let i = 0; i < commonChapters; i++) {
    const a = current[i]!;
    const b = baseline[i]!;
    if (a.title !== b.title || a.header_pattern !== b.header_pattern) count += 1;
    const subCountDelta = Math.abs(a.sub_sections.length - b.sub_sections.length);
    count += subCountDelta;
    const commonSubs = Math.min(a.sub_sections.length, b.sub_sections.length);
    for (let j = 0; j < commonSubs; j++) {
      const as = a.sub_sections[j]!;
      const bs = b.sub_sections[j]!;
      const metaChanged =
        as.label !== bs.label ||
        as.header_pattern !== bs.header_pattern ||
        (as.excluded ?? false) !== (bs.excluded ?? false);
      if (metaChanged) count += 1;
      // Each paragraph-override delta counts as a separate edit — a
      // rewrite of §1.4 ¶3 and an exclude of §1.4 ¶7 are two actions
      // in the reviewer's head, so they should be two in the chip too.
      count += countOverrideDeltas(as.paragraph_overrides, bs.paragraph_overrides);
    }
  }
  return count;
}

/** Count override differences between two arrays for the unsaved-edits
 *  chip. Added or removed overrides each count as 1; modified overrides
 *  (same kind + index, different text) count as 1. */
function countOverrideDeltas(
  current: readonly ParagraphOverride[] | undefined,
  baseline: readonly ParagraphOverride[] | undefined,
): number {
  const curr = current ?? [];
  const base = baseline ?? [];
  if (curr.length === 0 && base.length === 0) return 0;
  // Fast path: order-identical arrays, same length → compare position-wise.
  if (curr.length === base.length) {
    let diffs = 0;
    for (let i = 0; i < curr.length; i += 1) {
      const x = curr[i]!;
      const y = base[i]!;
      if (x.kind !== y.kind || x.paragraph_index !== y.paragraph_index) {
        diffs += 1;
      } else if (x.kind === 'rewrite' && y.kind === 'rewrite' && x.replacement_text !== y.replacement_text) {
        diffs += 1;
      } else if (x.kind === 'insert_after' && y.kind === 'insert_after' && x.inserted_text !== y.inserted_text) {
        diffs += 1;
      }
    }
    return diffs;
  }
  // Different lengths → treat each unmatched entry as a delta.
  return Math.abs(curr.length - base.length) + Math.min(curr.length, base.length);
}

/** Apply (upsert or replace) a single paragraph override on the current
 *  sub-section. For `rewrite` + `exclude`, we REPLACE any existing
 *  override of the same kind at the same paragraph_index (validation
 *  requires at most one of each per index). For `insert_after`, we
 *  APPEND — multiple inserts at the same slot are allowed in order,
 *  matching the chunker's left-to-right apply semantics. */
function applyParagraphOverride(
  chapter: ResolvedChapter,
  subIdx: number,
  override: ParagraphOverride,
): ResolvedChapter | null {
  if (subIdx < 0 || subIdx >= chapter.sub_sections.length) return null;
  const sub = chapter.sub_sections[subIdx]!;
  const current = sub.paragraph_overrides ?? [];
  let nextOverrides: ParagraphOverride[];
  if (override.kind === 'insert_after') {
    nextOverrides = [...current, override];
  } else {
    // Replace existing override of the same kind + index; keep everything else.
    const filtered = current.filter(
      (o) => !(o.kind === override.kind && o.paragraph_index === override.paragraph_index),
    );
    nextOverrides = [...filtered, override];
  }
  const nextSub: ResolvedSubSection = { ...sub, paragraph_overrides: nextOverrides };
  return {
    ...chapter,
    sub_sections: chapter.sub_sections.map((s, i) => (i === subIdx ? nextSub : s)),
  };
}

function applySectionAction(
  chapter: ResolvedChapter,
  subIdx: number,
  action: SectionAction,
): ResolvedChapter | null {
  if (subIdx < 0 || subIdx >= chapter.sub_sections.length) return null;
  const sub = chapter.sub_sections[subIdx]!;

  if (action === 'toggle-excluded') {
    const next = { ...sub, excluded: !sub.excluded };
    return {
      ...chapter,
      sub_sections: chapter.sub_sections.map((s, i) => (i === subIdx ? next : s)),
    };
  }

  if (action === 'delete-section') {
    return {
      ...chapter,
      sub_sections: chapter.sub_sections.filter((_, i) => i !== subIdx),
    };
  }

  return null;
}
